/* ============================================================================
   haiku-preview-fx — the photographs dissolve through a turbulent flow field

   WHAT THIS REPLACES
   haiku-preview.js paged between a product's photographs with an opacity
   crossfade: 260ms of two images at partial alpha. Legible, but it is the
   default any <img> gets for free, and it says nothing.

   THE TECHNIQUE, and why this one
   A curl-noise displacement dissolve. Three things happen at once on the GPU:

     1. FLOW      A 2D simplex field is differentiated into a CURL field —
                  divergence-free, so it swirls rather than pumping pixels into
                  and out of existence. The outgoing photograph is dragged along
                  it, the incoming one arrives against it. Divergence-free is
                  the whole reason this reads as ink turning in water and not as
                  a wobble: nothing pools, nothing tears.

     2. DISSOLVE  The reveal is not a fade. A second, coarser noise field is
                  thresholded against progress, so the new photograph arrives in
                  blooms that open where the field is low and close last where
                  it is high — with a directional gradient mixed in so the
                  motion still reads as "forward" on next and "back" on prev.

     3. DRAG      Displacement is scaled by 1-|2m-1|, so it PEAKS exactly at the
                  dissolve boundary and is zero in the settled areas. The
                  smearing happens only along the moving edge, where the eye
                  already is; the parts of each photograph you can read stay
                  readable. Chromatic aberration rides the same term, so the
                  colour split exists only where something is actually moving.

   Both ends are exact by construction, not by tuning: the sin(pi*p) envelope is
   zero at p=0 and p=1, both zoom terms resolve to 1.0 there, and the threshold
   sweeps clear of the whole 0..1 band — so frame one IS the outgoing photograph
   and the last frame IS the incoming one. Nothing is left half-warped.

   WHY RAW WebGL2 AND NOT THREE
   Three.js does ship in this theme, but a two-texture transition is one quad,
   two samplers and a float. haiku-mark-distort.js already proves a hand-written
   layer is the right size for this job. No bundle step, no import map, nothing
   for Shopify's flat asset directory to fail to resolve.

   ADDITIVE AND FAIL-SAFE — the rule that matters
   haiku-distort once blanked the entire product grid by hiding the DOM <img> as
   soon as `mesh.visible` was true: a flag it had set itself, not evidence that
   anything reached the screen. So here the canvas is drawn at progress 0 —
   which is by definition the photograph already on screen — read back with
   gl.readPixels, and required to contain real, VARYING ink. Only then is the
   DOM image hidden. Every other path (no WebGL2, shader will not link, image
   blocked by CORS, load timeout, proof fails, anything throws) calls the
   fallback the caller handed us, which is the original opacity crossfade,
   unchanged.

   The canvas also hands back at the end: the DOM <img> is given the new src,
   decoded, revealed underneath a canvas still showing the same frame, and only
   then is the canvas dropped. The resting state of the preview is always the
   real <img>, never a canvas — so nothing downstream has to know this exists.

   snoise() is Ashima Arts' 2D simplex noise (Ian McEwan, MIT).
   https://github.com/ashima/webgl-noise
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.haikuPreviewFxConfig || {};
  if (cfg.enabled === false) return;

  var mm = function (q) { return !!(window.matchMedia && window.matchMedia(q).matches); };
  if (mm('(prefers-reduced-motion: reduce)')) return;   // -> opacity crossfade
  /* Touch used to be an unconditional decline. It is now a default, not a
     law: the owner wants this transition on the phone too, where it is the
     whole point of opening a photograph. `allowTouch: false` restores the
     old behaviour if a device turns out to struggle. */
  if (mm('(hover: none)') && cfg.allowTouch !== true) return;   // -> opacity crossfade

  var num = function (v, d) { return typeof v === 'number' ? v : d; };

  var DURATION = num(cfg.duration,   1100);
  var AMP      = num(cfg.amplitude,  0.085);
  var ABER     = num(cfg.aberration, 0.004);
  var SCALE    = num(cfg.scale,      2.6);
  var EDGE     = num(cfg.edge,       0.34);
  var ZOOM     = num(cfg.zoom,       0.045);
  var LEAD     = num(cfg.lead,       0.35);
  var MAXPX    = num(cfg.maxWidth,   1800);
  var TIMEOUT  = num(cfg.timeout,    2600);

  /* ── shaders ───────────────────────────────────────────────────────────── */

  var VERT = '#version 300 es\n' +
    'in vec2 aPos;out vec2 vUv;void main(){vUv=aPos*0.5+0.5;gl_Position=vec4(aPos,0.0,1.0);}';

  var FRAG = '#version 300 es\n' + [
    'precision highp float;',
    'in vec2 vUv; out vec4 outColor;',
    'uniform sampler2D uFrom, uTo;',
    'uniform vec2 uFromFit, uToFit;',
    'uniform float uProgress, uDir, uAmp, uAber, uScale, uEdge, uZoom, uLead, uSeed;',

    /* Ashima Arts 2D simplex noise (Ian McEwan, MIT) */
    'vec3 permute(vec3 x){ return mod(((x*34.0)+1.0)*x, 289.0); }',
    'float snoise(vec2 v){',
    '  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);',
    '  vec2 i  = floor(v + dot(v, C.yy));',
    '  vec2 x0 = v - i + dot(i, C.xx);',
    '  vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);',
    '  vec4 x12 = x0.xyxy + C.xxzz; x12.xy -= i1;',
    '  i = mod(i, 289.0);',
    '  vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0)) + i.x + vec3(0.0, i1.x, 1.0));',
    '  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);',
    '  m = m*m; m = m*m;',
    '  vec3 x = 2.0 * fract(p * C.www) - 1.0;',
    '  vec3 h = abs(x) - 0.5;',
    '  vec3 ox = floor(x + 0.5);',
    '  vec3 a0 = x - ox;',
    '  m *= 1.79284291400159 - 0.85373472095314 * (a0*a0 + h*h);',
    '  vec3 g;',
    '  g.x  = a0.x  * x0.x  + h.x  * x0.y;',
    '  g.yz = a0.yz * x12.xz + h.yz * x12.yw;',
    '  return 130.0 * dot(m, g);',
    '}',

    /* curl of the scalar field: divergence-free, so it swirls and never pools */
    'vec2 curl(vec2 p){',
    '  float e = 0.045;',
    '  float n1 = snoise(p + vec2(0.0,  e));',
    '  float n2 = snoise(p + vec2(0.0, -e));',
    '  float n3 = snoise(p + vec2( e, 0.0));',
    '  float n4 = snoise(p + vec2(-e, 0.0));',
    '  vec2 f = vec2(n1 - n2, n4 - n3) / (2.0 * e);',
    /* soft clamp: keeps the field's variation below unit length, bounds it above */
    '  return f / max(1.0, length(f));',
    '}',

    /* object-fit: cover, in uv space, so the canvas frames exactly as the <img> */
    'vec2 fit(vec2 uv, vec2 s){ return (uv - 0.5) * s + 0.5; }',
    'vec2 zoom(vec2 uv, float z){ return (uv - 0.5) / z + 0.5; }',

    'void main(){',
    '  vec2 uv = vec2(vUv.x, 1.0 - vUv.y);',
    '  float p = uProgress;',

    '  vec2 flow = curl(uv * uScale + uSeed);',

    /* the dissolve band: coarse noise, biased by a directional gradient */
    '  float field = snoise(uv * (uScale * 0.62) - uSeed * 0.7) * 0.5 + 0.5;',
    '  float grad  = mix(uv.x, 1.0 - uv.y, 0.4);',
    '  grad = mix(1.0 - grad, grad, step(0.0, uDir));',
    '  float band = clamp(mix(field, grad, 0.42), 0.0, 1.0);',
    /* t sweeps past both ends of band by uEdge, so p=0 and p=1 are pure */
    '  float t = p * (1.0 + 2.0 * uEdge) - uEdge;',
    '  float m = 1.0 - smoothstep(t - uEdge, t + uEdge, band);',

    '  float env = pow(max(sin(p * 3.14159265), 0.0), 0.8);',
    /* drag peaks on the dissolve boundary, vanishes in settled areas */
    '  float drag = env * (0.32 + 0.68 * (1.0 - abs(2.0 * m - 1.0)));',

    '  vec2 push = flow * uAmp * drag;',
    '  vec2 lead = vec2(uDir, 0.0) * uAmp * uLead * drag;',

    '  float zf = 1.0 + uZoom * p;',
    '  float zt = 1.0 + uZoom * (1.0 - p);',
    '  vec2 uvF = fit(zoom(uv + push + lead, zf), uFromFit);',
    '  vec2 uvT = fit(zoom(uv - push * 0.8 - lead, zt), uToFit);',

    '  vec2 ab = flow * uAber * drag;',
    '  vec3 cF = vec3(texture(uFrom, uvF + ab).r, texture(uFrom, uvF).g, texture(uFrom, uvF - ab).b);',
    '  vec3 cT = vec3(texture(uTo,   uvT + ab).r, texture(uTo,   uvT).g, texture(uTo,   uvT - ab).b);',

    '  outColor = vec4(mix(cF, cT, m), 1.0);',
    '}'
  ].join('\n');

  function sh(gl, type, src) {
    var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { gl.deleteShader(s); return null; }
    return s;
  }
  function linkProgram(gl, vsrc, fsrc) {
    var v = sh(gl, gl.VERTEX_SHADER, vsrc), f = sh(gl, gl.FRAGMENT_SHADER, fsrc);
    if (!v || !f) return null;
    var p = gl.createProgram(); gl.attachShader(p, v); gl.attachShader(p, f);
    gl.bindAttribLocation(p, 0, 'aPos'); gl.linkProgram(p);
    return gl.getProgramParameter(p, gl.LINK_STATUS) ? p : null;
  }

  /* ── state ─────────────────────────────────────────────────────────────── */

  var G = null;        // { gl, canvas, prog, u, fig, w, h }
  var proven = false;  // a frame has been read back and contained real ink
  var dead = false;    // gave up for good; every call falls back from here on
  var owning = false;  // canvas is visible and the DOM <img> is hidden
  var curSrc = '';     // the photograph the canvas is currently showing
  var cache = {};      // src -> { tex, w, h }
  var order = [];      // cache keys, oldest first
  var CACHE_MAX = 10;
  var run = null;      // the in-flight transition

  /* ── setup ─────────────────────────────────────────────────────────────── */

  function setup(fig) {
    if (dead) return null;
    if (G && G.fig === fig) return G;

    var canvas = document.createElement('canvas');
    canvas.className = 'hpfx-canvas';
    canvas.setAttribute('aria-hidden', 'true');

    var gl = canvas.getContext('webgl2', {
      alpha: false, antialias: false, preserveDrawingBuffer: true
    });
    if (!gl) { dead = true; return null; }

    var prog = linkProgram(gl, VERT, FRAG);
    if (!prog) { dead = true; return null; }

    gl.bindVertexArray(gl.createVertexArray());
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    var names = ['uFrom', 'uTo', 'uFromFit', 'uToFit', 'uProgress', 'uDir',
                 'uAmp', 'uAber', 'uScale', 'uEdge', 'uZoom', 'uLead', 'uSeed'];
    var u = {};
    for (var i = 0; i < names.length; i++) u[names[i]] = gl.getUniformLocation(prog, names[i]);

    /* Anisotropic filtering if the driver has it: the photographs are cropped
       hard on one axis by the cover mapping, which is exactly the case
       isotropic mips blur. Optional — absent, the mips alone are enough. */
    var ext = gl.getExtension('EXT_texture_filter_anisotropic') ||
              gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');
    var aniso = ext ? {
      MAX: ext.TEXTURE_MAX_ANISOTROPY_EXT,
      max: Math.min(4, gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT) || 1)
    } : null;

    fig.appendChild(canvas);
    G = { gl: gl, canvas: canvas, prog: prog, u: u, fig: fig, w: 0, h: 0, aniso: aniso };
    return G;
  }

  function resize() {
    var r = G.fig.getBoundingClientRect();
    if (!r.width || !r.height) return false;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = Math.round(r.width * dpr), h = Math.round(r.height * dpr);
    if (w > MAXPX) { h = Math.round(h * (MAXPX / w)); w = MAXPX; }
    w = Math.max(2, w); h = Math.max(2, h);
    if (w !== G.w || h !== G.h) { G.canvas.width = w; G.canvas.height = h; G.w = w; G.h = h; }
    return true;
  }

  /* ── textures ──────────────────────────────────────────────────────────── */

  /* Every URL that reaches this file is normalised first, and it matters more
     than it looks. data-media carries Shopify's protocol-relative form
     (//cdn.shopify.com/...) while img.currentSrc hands back the resolved
     absolute URL — so keying the cache on the raw strings meant the outgoing
     photograph missed on EVERY transition and was re-fetched, re-decoded and
     re-mipmapped before the tween could start. Measured before this: the
     displacement was still ramping up 1.0s into a 1.1s transition, because the
     transition had not begun when the click did. */
  var _resolve = document.createElement('a');
  function norm(u) {
    if (!u) return '';
    _resolve.href = u;
    return _resolve.href;
  }

  function loadTex(raw, done) {
    var src = norm(raw);                    // defensive: keys are always resolved
    if (cache[src]) return done(cache[src]);
    var img = new Image();
    /* Anonymous CORS is not optional: without it the upload taints the canvas
       and gl.readPixels throws, so the proof below could never run. Shopify's
       CDN answers `access-control-allow-origin: *`. */
    img.crossOrigin = 'anonymous';
    var settled = false;
    var timer = window.setTimeout(function () {
      if (settled) return;
      settled = true; done(null);
    }, TIMEOUT);
    img.onload = function () {
      if (settled) return;
      settled = true; window.clearTimeout(timer);
      var gl = G.gl, rec;
      try {
        var t = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, t);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        /* Mipmaps are not decoration here. These are 1400px photographs landing
           in a ~720px box — a 1.9x minification, which plain LINEAR filtering
           undersamples: the shader frames would come out crunchier than the
           <img> they sit over, and the handover at the end would read as a snap
           into focus. Measured on the live theme with trilinear on, the final
           frame differs from the photograph itself by 1.22 mean channel levels
           out of 255 across the whole picture. WebGL2 mipmaps NPOT textures, so
           there is no power-of-two dance to do. */
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        if (G.aniso) gl.texParameterf(gl.TEXTURE_2D, G.aniso.MAX, G.aniso.max);
        rec = { tex: t, w: img.naturalWidth || 1, h: img.naturalHeight || 1 };
      } catch (e) { return done(null); }
      cache[src] = rec; order.push(src);
      while (order.length > CACHE_MAX) {
        var old = order.shift();
        if (cache[old] && old !== src && old !== curSrc) {
          gl.deleteTexture(cache[old].tex); delete cache[old];
        }
      }
      done(rec);
    };
    img.onerror = function () {
      if (settled) return;
      settled = true; window.clearTimeout(timer); done(null);
    };
    img.src = src;
  }

  /* cover: the axis that overflows is sampled over a narrower range */
  function fitOf(rec) {
    var ca = G.w / G.h, ia = rec.w / rec.h;
    return ia > ca ? [ca / ia, 1] : [1, ia / ca];
  }

  /* ── draw ──────────────────────────────────────────────────────────────── */

  function draw(p) {
    var gl = G.gl, u = G.u, r = run;
    gl.viewport(0, 0, G.w, G.h);
    gl.useProgram(G.prog);
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, r.from.tex); gl.uniform1i(u.uFrom, 0);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, r.to.tex);   gl.uniform1i(u.uTo, 1);
    var ff = fitOf(r.from), tf = fitOf(r.to);
    gl.uniform2f(u.uFromFit, ff[0], ff[1]);
    gl.uniform2f(u.uToFit, tf[0], tf[1]);
    gl.uniform1f(u.uProgress, p);
    gl.uniform1f(u.uDir, r.dir);
    gl.uniform1f(u.uAmp, AMP);
    gl.uniform1f(u.uAber, ABER);
    gl.uniform1f(u.uScale, SCALE);
    gl.uniform1f(u.uEdge, EDGE);
    gl.uniform1f(u.uZoom, ZOOM);
    gl.uniform1f(u.uLead, LEAD);
    gl.uniform1f(u.uSeed, r.seed);
    gl.disable(gl.BLEND);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  /* THE PROOF. draw(0) is, by construction, the photograph already on screen.
     Read three rows back and require real, VARYING ink — a uniform readback
     would mean a cleared or black buffer, which is exactly the failure that
     blanked the product grid last time. Only a pass may hide the DOM image. */
  function prove() {
    var gl = G.gl;
    while (gl.getError() !== gl.NO_ERROR) { /* drain */ }
    var n = Math.min(G.w, 256);
    var px = new Uint8Array(n * 4);
    var rows = [Math.floor(G.h * 0.25), Math.floor(G.h * 0.5), Math.floor(G.h * 0.75)];
    var passes = 0;
    for (var r = 0; r < rows.length; r++) {
      try { gl.readPixels(0, rows[r], n, 1, gl.RGBA, gl.UNSIGNED_BYTE, px); }
      catch (e) { return false; }                       // tainted canvas, etc.
      if (gl.getError() !== gl.NO_ERROR) return false;
      var lo = 255, hi = 0, sum = 0;
      for (var i = 0; i < px.length; i += 4) {
        var l = (px[i] * 299 + px[i + 1] * 587 + px[i + 2] * 114) / 1000;
        if (l < lo) lo = l;
        if (l > hi) hi = l;
        sum += l;
      }
      if (hi - lo > 6 || sum / n > 12) passes++;
    }
    return passes >= 2;
  }

  /* ── the DOM handover ──────────────────────────────────────────────────── */

  function takeOver(img) {
    G.fig.classList.add('hpfx-on');   // canvas becomes visible, showing frame 0
    img.style.transition = 'none';    // never animate the handover itself
    img.style.opacity = '0';
    owning = true;
  }

  function dropCanvas() {
    if (G) G.fig.classList.remove('hpfx-on');
    owning = false;
  }

  function showImg(img) {
    if (!img) return;
    img.style.transition = '';
    img.style.opacity = '';
  }

  /* The canvas is still showing the final frame. Give the <img> the same
     photograph, decode it, reveal it underneath, and only then drop the
     canvas — two identical frames, so there is nothing to see. */
  function handBack(img, src, after) {
    curSrc = src;
    var done = function () {
      showImg(img);
      window.requestAnimationFrame(function () {
        dropCanvas();
        if (after) after();
      });
    };
    if (norm(img.getAttribute('src')) !== src) img.src = src;
    if (img.decode) img.decode().then(done).catch(done);
    else window.setTimeout(done, 60);
  }

  /* ── the tween ─────────────────────────────────────────────────────────── */

  /* power3.inOut — the same easing vocabulary as the gallery's Flip tween */
  function ease(x) {
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  }

  function animate() {
    var r = run, t0 = performance.now();
    var tick = function (now) {
      if (run !== r) return;
      var p = Math.min(1, (now - t0) / DURATION);
      draw(ease(p));
      if (p < 1) { r.raf = window.requestAnimationFrame(tick); return; }
      r.raf = 0; run = null;
      handBack(r.img, r.src);
    };
    r.raf = window.requestAnimationFrame(tick);
  }

  /* ── public entry point ────────────────────────────────────────────────── */

  /* Give up cleanly and let the caller's crossfade do the work. If the canvas
     is currently owning the image, the <img> is first put back to exactly what
     the canvas is showing, so the crossfade starts from the visible frame. */
  function giveUp(img, fallback) {
    if (!owning || !img) { dropCanvas(); showImg(img); fallback(); return; }
    var go = function () { showImg(img); dropCanvas(); fallback(); };
    if (curSrc && norm(img.getAttribute('src')) !== curSrc) img.src = curSrc;
    if (img.decode) img.decode().then(go).catch(go);
    else go();
  }

  function transition(img, rawSrc, dir, fallback) {
    var fb = typeof fallback === 'function' ? fallback : function () {};
    try {
      if (dead || !img || !rawSrc) return giveUp(img, fb);
      var src = norm(rawSrc);

      var fig = img.parentNode;
      if (!fig || !fig.classList || !fig.classList.contains('content__preview-img')) return giveUp(img, fb);
      if (!setup(fig)) return giveUp(img, fb);
      if (!resize()) return giveUp(img, fb);

      /* A second click while one is running: snap the current transition to its
         end so the canvas shows that photograph, and page on from there. The
         canvas keeps owning throughout — no handover flicker between clicks. */
      if (run) {
        if (run.raf) window.cancelAnimationFrame(run.raf);
        if (run.from && run.to) { draw(1); curSrc = run.src; }
        run = null;
      }

      var fromSrc = owning && curSrc ? curSrc : norm(img.currentSrc || img.src);
      if (!fromSrc) return giveUp(img, fb);
      if (fromSrc === src) return giveUp(img, fb);   // nothing to transition to

      var token = {};
      run = {
        token: token, img: img, src: src, dir: dir < 0 ? -1 : 1,
        seed: Math.random() * 12, raf: 0, from: null, to: null
      };

      loadTex(fromSrc, function (from) {
        if (!run || run.token !== token) return;
        if (!from) { run = null; return giveUp(img, fb); }
        loadTex(src, function (to) {
          if (!run || run.token !== token) return;
          if (!to) { run = null; return giveUp(img, fb); }
          try {
            run.from = from; run.to = to;
            if (!resize()) { run = null; return giveUp(img, fb); }
            draw(0);
            if (!proven) proven = prove();
            if (!proven) { dead = true; run = null; return giveUp(img, fb); }
            takeOver(img);
            animate();
          } catch (e) { run = null; giveUp(img, fb); }
        });
      });
    } catch (e) { run = null; giveUp(img, fb); }
  }

  /* Warm the whole pipeline while the preview settles — the context, the
     shader compile, and every photograph of the open product — so the first
     click pays for none of it. Purely opportunistic: bails on anything. */
  function prefetch(list) {
    if (dead || !list || !list.length) return;
    var img = document.querySelector('.content__preview-img img');
    if (!img || !img.parentNode) return;
    if (!setup(img.parentNode)) return;
    var i = 0;
    var next = function () {
      if (dead || i >= list.length) return;
      var s = norm(list[i++]);
      if (cache[s]) return next();
      loadTex(s, function () { window.setTimeout(next, 40); });
    };
    if (window.requestIdleCallback) window.requestIdleCallback(next, { timeout: 1200 });
    else window.setTimeout(next, 400);
  }

  /* The preview closing (or reopening) must never leave a stale canvas on
     screen: haiku-gallery.js refills the <img> from the clicked thumbnail and
     runs its own 1.2s Flip on it. Watch hp-open specifically — <html> carries
     other classes that change for unrelated reasons. */
  var wasOpen = document.documentElement.classList.contains('hp-open');
  new MutationObserver(function () {
    var isOpen = document.documentElement.classList.contains('hp-open');
    if (isOpen === wasOpen) return;
    wasOpen = isOpen;
    if (!G) return;
    if (run && run.raf) window.cancelAnimationFrame(run.raf);
    var img = run ? run.img : G.fig.querySelector('img');
    run = null; curSrc = '';
    dropCanvas(); showImg(img);
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

  window.haikuPreviewFx = { transition: transition, prefetch: prefetch };
})();
